import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { dbOperations, priceHelpers } from '../../utils/supabase'
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  PhotoIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  GiftIcon
} from '@heroicons/react/24/outline'

const AddPackagePage = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState([])
  const [productSets, setProductSets] = useState([])
  const [categories, setCategories] = useState([])
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    package_price: '',
    discount_amount: '',
    category: '',
    is_active: true,
    is_featured: false,
    tags: ''
  })

  // Image handling
  const [uploadedImages, setUploadedImages] = useState([])
  const [uploadingImages, setUploadingImages] = useState(false)

  // Package items
  const [packageItems, setPackageItems] = useState([])
  const [selectedItemType, setSelectedItemType] = useState('product')
  const [selectedItemId, setSelectedItemId] = useState('')
  const [selectedQuantity, setSelectedQuantity] = useState('1')
  const [searchTerm, setSearchTerm] = useState('')
  const [showItemPicker, setShowItemPicker] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [productsResponse, productSetsResponse, categoriesResponse] = await Promise.all([
        dbOperations.products.getAll(),
        dbOperations.productSets.getAll(),
        dbOperations.categories.getAll()
      ])
      
      setProducts(productsResponse.data || [])
      setProductSets(productSetsResponse.data || [])
      setCategories(categoriesResponse.data || [])
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Image handling functions
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files)
    
    if (files.length === 0) return
    
    setUploadingImages(true)
    try {
      const uploadPromises = files.map(file => 
        dbOperations.storage.uploadImage(file)
      )
      
      const results = await Promise.all(uploadPromises)
      
      const successfulUploads = results
        .filter(result => !result.error)
        .map(result => ({
          url: result.url,
          fileName: result.fileName
        }))

      setUploadedImages(prev => [...prev, ...successfulUploads])
      console.log('Images uploaded successfully:', successfulUploads)
    } catch (error) {
      console.error('Error uploading images:', error)
      alert('Resim yüklenirken hata oluştu!')
    } finally {
      setUploadingImages(false)
    }
  }

  const removeUploadedImage = async (index) => {
    const imageToRemove = uploadedImages[index]
    try {
      if (imageToRemove.fileName) {
        await dbOperations.storage.deleteFile(imageToRemove.fileName)
      }
      setUploadedImages(uploadedImages.filter((_, i) => i !== index))
    } catch (error) {
      console.error('Error deleting image:', error)
    }
  }

    // Package items functions (eski addPackageItem kaldırıldı, selectItem kullanılıyor)

  const removePackageItem = (itemType, itemId) => {
    setPackageItems(packageItems.filter(item => 
      !(item.item_type === itemType && item.item_id === itemId) // === kullan
    ))
  }

  const updateItemQuantity = (itemType, itemId, newQuantity) => {
    if (newQuantity < 1) return
    
    setPackageItems(packageItems.map(item => 
      item.item_type === itemType && item.item_id === itemId // === kullan
        ? { ...item, quantity: newQuantity }
        : item
    ))
  }

  const calculateTotalPrice = () => {
    return packageItems.reduce((total, item) => total + (item.item_price * item.quantity), 0)
  }

  const calculateDiscountedPrice = () => {
    const manualPrice = parseFloat(formData.package_price) || 0
    const discount = parseFloat(formData.discount_amount) || 0
    return manualPrice - discount
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Otomatik fiyat hesapla eğer manuel girilmemişse
      let finalPrice = parseFloat(formData.package_price)
      if (!finalPrice && packageItems.length > 0) {
        finalPrice = calculateTotalPrice()
      }

      const packageData = {
        ...formData,
        package_price: finalPrice,
        discount_amount: parseFloat(formData.discount_amount) || 0,
        images: uploadedImages
      }

      console.log('Package data being sent:', packageData)

      // Yeni paket oluştur
      const { data: newPackage, error: packageError } = await dbOperations.packages.create(packageData)
      
      if (packageError) throw packageError

      // Paket öğelerini ekle
      if (packageItems.length > 0) {
        console.log('Adding package items:', packageItems)
        
        for (const item of packageItems) {
          console.log('Processing item:', item)
          
          if (!item.item_id) {
            console.error('Invalid item_id:', item.item_id)
            throw new Error(`Geçersiz ürün ID: ${item.item_id}`)
          }
          
          const itemData = {
            package_id: newPackage.id,
            item_type: item.item_type,
            item_id: item.item_id, // UUID string olarak bırak
            quantity: parseInt(item.quantity),
            price_per_unit: parseFloat(item.item_price)
          }
          
          console.log('Creating package item:', itemData)
          
          const { data: packageItemResult, error: packageItemError } = await dbOperations.packageItems.create(itemData)
          
          if (packageItemError) {
            console.error('Package item error:', packageItemError)
            throw new Error(`Paket öğesi eklenemedi: ${packageItemError.message}`)
          }
          
          console.log('Package item created:', packageItemResult)
        }
      }

      alert('Paket başarıyla oluşturuldu!')
      navigate('/admin/packages')
    } catch (error) {
      console.error('Error creating package:', error)
      alert('Paket oluşturulurken hata oluştu!')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount)
  }

  const getAvailableItems = () => {
    const items = selectedItemType === 'product' ? products : productSets
    if (!searchTerm) return items
    
    return items.filter(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }

  const selectItem = (item) => {
    setSelectedItemId(item.id)
    setShowItemPicker(false)
    
    // Otomatik olarak ürünü ekle
    const existingItem = packageItems.find(pkgItem => 
      pkgItem.item_type === selectedItemType && pkgItem.item_id === item.id
    )
    
    if (existingItem) {
      setPackageItems(packageItems.map(pkgItem => 
        pkgItem.item_type === selectedItemType && pkgItem.item_id === item.id
          ? { ...pkgItem, quantity: pkgItem.quantity + parseInt(selectedQuantity) }
          : pkgItem
      ))
    } else {
      setPackageItems([...packageItems, {
        item_type: selectedItemType,
        item_id: item.id,
        quantity: parseInt(selectedQuantity),
        item_name: item.name,
        item_price: selectedItemType === 'product' ? item.price : item.set_price
      }])
    }
    
    setSelectedQuantity('1')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/admin/packages')}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🎁 Yeni Paket Oluştur</h1>
          <p className="mt-1 text-sm text-gray-600">
            Ürünler ve takımları birleştirerek özel paketler oluşturun
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sol Taraf - Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Temel Bilgiler */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">📝 Paket Bilgileri</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Paket Adı *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Örn: Düğün Paketi, Ofis Paketi"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kategori
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Kategori Seçin</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Açıklama
                </label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Paket hakkında açıklama..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Paket Fiyatı (₺)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.package_price}
                    onChange={(e) => setFormData({...formData, package_price: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Boş bırakılırsa otomatik hesaplanır"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    İndirim Miktarı (₺)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.discount_amount}
                    onChange={(e) => setFormData({...formData, discount_amount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Etiketler
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({...formData, tags: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="etiket1, etiket2, etiket3"
                />
              </div>

              <div className="flex items-center space-x-6 mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Aktif</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Öne Çıkan</span>
                </label>
              </div>
            </div>

            {/* Paket İçeriği */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">📦 Paket İçeriği</h3>
              
              {/* Öğe Tipi ve Arama */}
              <div className="mb-4">
                <div className="flex items-center space-x-4 mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Öğe Tipi
                    </label>
                    <select
                      value={selectedItemType}
                      onChange={(e) => {
                        setSelectedItemType(e.target.value)
                        setSearchTerm('')
                        setShowItemPicker(false)
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="product">🛋️ Ürünler</option>
                      <option value="product_set">📦 Ürün Takımları</option>
                    </select>
                  </div>
                  
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adet
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={selectedQuantity}
                      onChange={(e) => setSelectedQuantity(e.target.value)}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                
                <button
                  type="button"
                  onClick={() => setShowItemPicker(true)}
                  className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-500 hover:text-indigo-600 transition-colors"
                >
                  <PlusIcon className="h-5 w-5 mx-auto mb-1" />
                  {selectedItemType === 'product' ? 'Ürün Ekle' : 'Takım Ekle'}
                </button>
              </div>

              {/* Seçilen Öğeler */}
              <div className="space-y-3">
                {packageItems.length > 0 ? (
                  packageItems.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-medium text-gray-500">
                            {item.item_type === 'product' ? '🛋️ Ürün' : '📦 Takım'}
                          </span>
                          <h4 className="text-sm font-medium text-gray-900">
                            {item.item_name}
                          </h4>
                        </div>
                        <p className="text-sm text-gray-500">
                          {formatCurrency(item.item_price)} × 
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateItemQuantity(item.item_type, item.item_id, parseInt(e.target.value))}
                            className="w-16 mx-1 px-1 py-0 text-center border border-gray-300 rounded text-sm"
                          />
                          = {formatCurrency(item.item_price * item.quantity)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePackageItem(item.item_type, item.item_id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <GiftIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">Henüz paket öğesi eklenmedi</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sağ Taraf - Resimler ve Özet */}
          <div className="space-y-6">
            {/* Resim Yükleme */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">📸 Paket Resimleri</h3>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="image-upload"
                />
                <label htmlFor="image-upload" className="cursor-pointer">
                  <PhotoIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">
                    Resim yüklemek için tıklayın
                  </p>
                </label>
              </div>

              {uploadingImages && (
                <div className="mt-2 text-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="text-xs text-gray-500 mt-1">Yükleniyor...</p>
                </div>
              )}

              {/* Yüklenmiş Resimler */}
              <div className="space-y-2 mt-4">
                {uploadedImages.map((image, index) => (
                  <div
                    key={index}
                    className="relative group border border-gray-200 rounded-lg p-2"
                  >
                    <div className="flex items-center space-x-3">
                      <img
                        src={image.url}
                        alt={`Resim ${index + 1}`}
                        className="w-12 h-12 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-900 truncate">
                          Resim {index + 1}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeUploadedImage(index)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Fiyat Özeti */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">💰 Fiyat Özeti</h3>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Toplam İçerik Değeri:</span>
                  <span className="font-medium">{formatCurrency(calculateTotalPrice())}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Paket Fiyatı:</span>
                  <span className="font-medium">
                    {formData.package_price 
                      ? formatCurrency(parseFloat(formData.package_price))
                      : formatCurrency(calculateTotalPrice())
                    }
                  </span>
                </div>
                
                {formData.discount_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">İndirim:</span>
                    <span className="font-medium text-red-600">
                      -{formatCurrency(parseFloat(formData.discount_amount))}
                    </span>
                  </div>
                )}
                
                <div className="border-t pt-2">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-900">Son Fiyat:</span>
                    <span className="font-bold text-indigo-600">
                      {formatCurrency(
                        formData.package_price 
                          ? calculateDiscountedPrice()
                          : calculateTotalPrice() - (parseFloat(formData.discount_amount) || 0)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/packages')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={loading || !formData.name}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? 'Oluşturuluyor...' : 'Paket Oluştur'}
          </button>
        </div>
      </form>

      {/* Item Picker Modal */}
      {showItemPicker && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedItemType === 'product' ? '🛋️ Ürün Seç' : '📦 Takım Seç'}
              </h3>
              <button
                onClick={() => setShowItemPicker(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Arama */}
            <div className="mb-4">
              <input
                type="text"
                placeholder={`${selectedItemType === 'product' ? 'Ürün' : 'Takım'} ara...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-96 overflow-y-auto">
              {getAvailableItems().map(item => (
                <div
                  key={item.id}
                  onClick={() => selectItem(item)}
                  className="border border-gray-200 rounded-lg p-3 cursor-pointer hover:border-indigo-500 hover:shadow-md transition-all"
                >
                  {/* Resim */}
                  <div className="h-32 bg-gray-100 rounded-md mb-2 flex items-center justify-center">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover rounded-md"
                      />
                    ) : (
                      <div className="text-gray-400">
                        {selectedItemType === 'product' ? '🛋️' : '📦'}
                      </div>
                    )}
                  </div>
                  
                  {/* İsim */}
                  <h4 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">
                    {item.name}
                  </h4>
                  
                  {/* Fiyat */}
                  <p className="text-sm font-bold text-indigo-600">
                    {formatCurrency(selectedItemType === 'product' ? item.price : item.set_price)}
                  </p>
                  
                  {/* Kategori */}
                  {item.category && (
                    <p className="text-xs text-gray-500 mt-1">
                      {item.category}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Sonuç bulunamadı */}
            {getAvailableItems().length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {searchTerm ? 'Arama kriterinize uygun sonuç bulunamadı' : 'Henüz ürün/takım bulunmuyor'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AddPackagePage 